import { useRequiredContext } from "@redotech/react-util/context";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import AlertCircleFilledIcon from "@redotech/redo-icons/alert-circle_filled.svg";
import CreditCard01Icon from "@redotech/redo-icons/credit-card-01.svg";
import CreditCard01FilledIcon from "@redotech/redo-icons/credit-card-01_filled.svg";
import CurrencyDollarIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import CurrencyDollarFilledIcon from "@redotech/redo-icons/currency-dollar-circle_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import DataflowIcon from "@redotech/redo-icons/dataflow-03.svg";
import DataflowFilledIcon from "@redotech/redo-icons/dataflow-03_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import SettingsFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import ShieldDollarFilledIcon from "@redotech/redo-icons/shield-dollar_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useMatch } from "react-router-dom";
import * as modalStyles from "../../../../finance/disputes/disputes.module.css";
import { useDisputeAlertsEnrollmentEnabled } from "../../../../finance/disputes/use-dispute-alerts-enrollment-enabled";

export function useFinanceNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  const disputesEnabled = !!team?.settings.disputes?.enabled;

  const primaryTab: LinkPrimaryTab = {
    id: "finance",
    hasNotification: false,
    UnfilledIcon: CurrencyDollarIcon,
    FilledIcon: CurrencyDollarFilledIcon,
    type: "link",
    href: routes.disputesDashboardHref,
    pathRoot: routes.financeBaseUrl,
    friendlyName: "Finance",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "finance",
    name: "Finance",
    NavContent: SecondaryFinanceNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: disputesEnabled,
  };
}

import {
  RECLAIM_IMAGE_URL,
  RECLAIM_POSTER_URL,
  RECLAIM_VIDEO_URL,
} from "../../../../finance/disputes/reclaim-constants";

const SecondaryFinanceNavigation = memo(function SecondaryFinanceNavigation() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { enabled: alertsEnabled } = useDisputeAlertsEnrollmentEnabled();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Finance",
  });

  const disputesBaseUrl = routes.financeBaseUrl + "/disputes";
  const anyDisputeChildSelected = useMatch(`${disputesBaseUrl}/*`);

  return (
    <TreeContext.Provider value={nodeId}>
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.disputesDashboardHref }}
        anyChildSelected={!!anyDisputeChildSelected}
        beta
        FilledIcon={ShieldDollarFilledIcon}
        friendlyName="Reclaim"
        id="disputes"
        UnfilledIcon={ShieldDollarIcon}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesDashboardHref }}
          FilledIcon={DashboardFilledIcon}
          friendlyName="Dashboard"
          id="disputes-dashboard"
          UnfilledIcon={DashboardIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesChargebacksHref }}
          FilledIcon={CreditCard01FilledIcon}
          friendlyName="Chargeback disputes"
          id="disputes-chargebacks"
          UnfilledIcon={CreditCard01Icon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesAlertsHref }}
          FilledIcon={AlertCircleFilledIcon}
          friendlyName="Alerts"
          hidden={!alertsEnabled}
          id="disputes-alerts"
          UnfilledIcon={AlertCircleIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesSettingsHref }}
          FilledIcon={SettingsFilledIcon}
          friendlyName="Settings"
          id="disputes-settings"
          UnfilledIcon={SettingsIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.disputesIntegrationsHref }}
          FilledIcon={DataflowFilledIcon}
          friendlyName="Integrations"
          id="disputes-integrations"
          UnfilledIcon={DataflowIcon}
        />
      </RedoSecondaryNavItem>
      {!!anyDisputeChildSelected && (
        <Flex
          align="center"
          cursor="pointer"
          gap="md"
          onClick={() => setShowVideoModal(true)}
          position="fixed"
          px="lg"
          py="md"
          style={{ bottom: 16, left: 80, width: 224 }}
        >
          <Flex
            align="center"
            borderStyle="solid"
            borderWidth="1px"
            justify="center"
            radius="xs"
            shrink={0}
            style={{
              width: 28,
              height: 28,
              background: "rgba(255, 255, 255, 0.1)",
              borderColor: "rgba(255, 255, 255, 0.15)",
              color: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <PlayCircleIcon height={14} width={14} />
          </Flex>
          <Flex dir="column" gap="xxs">
            <Text
              fontSize="xs"
              fontWeight="medium"
              style={{ color: "rgba(255, 255, 255, 0.9)" }}
            >
              How Reclaim AI works
            </Text>
            <Text fontSize="xxs" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
              Watch the 2-min video
            </Text>
          </Flex>
        </Flex>
      )}
      {showVideoModal && (
        <RedoModal
          hideFooter
          hideHeader
          isOpen
          modalSize="md"
          muiClassName={modalStyles.backdropBlur}
          noPadding
          onModalCloseRequested={() => setShowVideoModal(false)}
          title="How Reclaim AI works"
        >
          <Flex
            overflow="hidden"
            position="relative"
            style={{ height: 160 }}
            topLeftRadius="md"
            topRightRadius="md"
          >
            <img
              alt=""
              src={RECLAIM_IMAGE_URL}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "saturate(1.2) contrast(1.2)",
              }}
            />
            <Flex
              position="absolute"
              style={{ inset: 0, background: "#262626", opacity: 0.65 }}
            />
            <Flex
              dir="column"
              gap="xs"
              position="absolute"
              style={{ top: 16, left: 20, zIndex: 2 }}
            >
              <Text
                fontFamily="serif"
                fontSize="5xl"
                fontWeight="regular"
                textColor="white"
              >
                How Reclaim AI works
              </Text>
              <Text
                fontSize="sm"
                style={{ color: "rgba(255, 255, 255, 0.85)" }}
              >
                Watch the two-minute video
              </Text>
            </Flex>
          </Flex>
          <Flex
            bgColor="primary"
            p="2xl"
            position="relative"
            radius="md"
            style={{ marginTop: -12 }}
          >
            <video
              controls
              playsInline
              poster={RECLAIM_POSTER_URL}
              src={RECLAIM_VIDEO_URL}
              style={{ width: "100%", borderRadius: 6, background: "#000" }}
            />
          </Flex>
        </RedoModal>
      )}
    </TreeContext.Provider>
  );
});
